import { Controller } from "stimulus";
import { debounce, getCSRFToken, setUpdatedCsrfMetaContent } from "../../util";
import { showFormInputError, hideFormInputError } from "../../util/gecko_primer";
import {
  SELECT_SIGN_IN_CTA_EVENT,
  SELECT_SIGN_IN_METHOD_CTA_EVENT
} from "../../mixpanel_config";
import { unconditionalTrackEvent } from "../../analytics";
import * as Sentry from "@sentry/browser";

const ACTIVE_CLASSES = ["tw-underline", "tw-decoration-primary-500", "tw-decoration-2", "tw-underline-offset-2", "!tw-cursor-default"];
const MAX_PASSWORD_LENGTH = 128;
const MIN_PASSWORD_LENGTH = 8;
const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_RX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W|_]).{8,128}$/;

export default class extends Controller {
  validEmail = false;
  emailInputValue = null;
  isEmailLogin = false;
  ssoSource = null;
  ssoEmail = null;
  continueWithEmailButtonInnerHTML = null;
  redirectedToSso = false;
  authModalTriggerOrigin = null;

  static targets = [
    "defaultShow", "defaultHide",
    "authForm", "authModal", "authModalWelcomeTitle", "authModalWelcomeBackTitle", "authModalSubtitle",
    "signUpPassword", "signUpSubmit", "loginSubmit", "signInEmailInput", "signUpEmailInput", "userEmail", "resendConfirmationEmailText", "analyticsOrigin",
    "ssoLoginSubtitle", "ssoLoginSubtitleText", "googleSsoButton", "appleSsoButton", "ssoEmail",
    "continueWithOptions", "continueWithEmailButton", "continueWithEmail", "loginWithPassword", "signupWithPassword", "agreeTosAndReadPolicyText"
  ];

  /**
   * START: Modal content control (tabbed navigation, etc).
   */
  connect() {
    this.continueWithEmailButtonInnerHTML = this.continueWithEmailButtonTarget.innerHTML; // Save the original content
    this.signInEmailInputTarget.value = "";
    this.signUpEmailInputTarget.value = "";

    if (window.location.href.includes("sign_in")) {
      this.authModalTriggerOrigin = "login";
    } else if (window.location.href.includes("sign_up")) {
      this.authModalTriggerOrigin = "sign_up";
    }

    // Prevent default enter key behaviour to avoid form submission on only email input
    window.addEventListener("keydown", e => {
      if (e.key === "Enter") {
        e.preventDefault();
      }
    });

    window.addEventListener("gecko-modal-close", (event) => {
      if (event.detail.modalId === "auth_modal") {
        this.reset();
      }
    });
  }

  openSignInModal(e) {
    this.authModalTriggerOrigin = "login"

    if (!this._hasAuthModalOnPage()) {
      return window.location = `/account/sign_in?locale=${I18n.locale}`;
    }

    this.updateSubtitle(e);
    Modal.show("auth_modal");
    this._trackSelectSignInCTAEvent(e)
  }

  openSignUpModal(e) {
    this.authModalTriggerOrigin = "sign_up"

    if (!this._hasAuthModalOnPage()) {
      return window.location = `/account/sign_up?locale=${I18n.locale}`;
    }

    this.updateSubtitle(e)
    Modal.show("auth_modal");
    this._trackSelectSignInCTAEvent(e)
  }

  openResendConfirmationModal() {
    if (!this._hasAuthModalOnPage()) {
      return window.location = `/account/confirmation/new?locale=${I18n.locale}`;
    }

    Modal.show("resend_confirmation_modal");
  }

  openResetPasswordModal() {
    if (!this._hasAuthModalOnPage()) {
      return window.location = `/account/password/new?locale=${I18n.locale}`;
    }

    Modal.show("reset_password_modal");
  }

  updateSubtitle(e) {
    let authModalSubtitle = document.querySelector("[data-sign-in-title]")?.dataset.signInTitle
    if (authModalSubtitle) {
      this.authModalSubtitleTarget.innerText = authModalSubtitle;
    }
  }

  _hasAuthModalOnPage() {
    return !!document.getElementById("auth-modal");
  }

  showEmailInput() {
    this.continueWithEmailTarget.classList.remove("tw-hidden");
    this.continueWithEmailButtonTarget.disabled = true;
    setTimeout( () => { this.signInEmailInputTarget.focus() }, 10 );
  }

  async cee() {
    const email = this.emailInputValue;

    if (email?.length > 0) {
      this._showLoadingSpinner();

      try {
        await setUpdatedCsrfMetaContent();

        const response = await fetch(`/accounts/cee`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": getCSRFToken(),
          },
          body: JSON.stringify({ email: email }),
        });

        if (!response.ok) {
          Toaster.toast(I18n.t("auth.request_verification_failed"), {
            icon: {
              classes: "far fa-times tw-text-danger-500"
            },
            action: {
              message: "support@coingecko.com",
              options: {
                href: "mailto:support@coingecko.com",
                class: "tw-inline tw-cursor-pointer tw-mt-1 tw-underline tw-text-inherit hover:tw-text-primary-500"
              }
            }
          })

          this.reset();
          return;
        }

        const data = await response.json();
        this._handleAuthFlow({
          existence: data.e,
          signUpSource: data.s
        });
      } catch (e) {
        Sentry.captureException(e);
      }
    }
  }

  trackSignInMethodCta(e = null, method = null, origin = null) {
    const methodProperty = method || e?.currentTarget?.dataset?.ssoType || "email";
    const originProperty = origin || e?.currentTarget?.dataset?.analyticsOrigin || (this.isEmailLogin ? "login" : this.authModalTriggerOrigin);
    const eventProperties = {
      method: methodProperty,
      origin: originProperty,
    };

    if (!this.isEmailLogin) {
      eventProperties["redirected"] = this.redirectedToSso;
    }

    unconditionalTrackEvent(SELECT_SIGN_IN_METHOD_CTA_EVENT, eventProperties);
  }

  trackSignUpMethodCta(e) {
    const originProperty = e?.currentTarget?.dataset?.analyticsOrigin || "sign_up";

    const eventProperties = {
      method: "email",
      origin: originProperty,
    };

    unconditionalTrackEvent(SELECT_SIGN_IN_METHOD_CTA_EVENT, eventProperties);
  }

  _showLoadingSpinner() {
    this.continueWithEmailButtonTarget.innerHTML = `
      <i class="far fa-spin fa-spinner-third tw-text-2xl dark:tw-text-moon-50"></i>
    `;
  }

  _handleAuthFlow({ existence, signUpSource }) {
    const emailExists = existence;

    if (["google", "apple"].includes(signUpSource)) {
      this.ssoSource = signUpSource;
      this.ssoEmail = this.signInEmailInputTarget.value;
      this.isEmailLogin = false;
    } else {
      this.isEmailLogin = true;
    }

    this._hideElements(
      this.continueWithOptionsTargets,
      this.authModalSubtitleTarget
    );

    emailExists ? this._showReturningUserFlow() : this._showSignupFlow();
  }

  _showReturningUserFlow() {
    this.authModalTriggerOrigin = "login";

    this._toggleElementVisibility(this.authModalWelcomeTitleTarget, true);
    this._toggleElementVisibility(this.authModalWelcomeBackTitleTarget, false);

    this.isEmailLogin ? this._showEmailLoginFlow() : this._showSsoLoginFlow();
  }

  _showEmailLoginFlow() {
    this.userEmailTarget.innerText = this.signInEmailInputTarget.value;
    this._toggleElementVisibility(this.loginWithPasswordTarget, false);
    this._toggleElementVisibility(this.resendConfirmationEmailTextTarget, false);
    this._toggleElementVisibility(this.agreeTosAndReadPolicyTextTarget, true);
  }

  _capitalizeSsoSource() {
    return this.ssoSource.charAt(0).toUpperCase() + this.ssoSource.slice(1).toLowerCase();
  }

  _showSsoLoginFlow() {
    this.redirectedToSso = true;

    this._toggleElementVisibility(this.ssoLoginSubtitleTarget, false);
    this._toggleElementVisibility(this.agreeTosAndReadPolicyTextTarget, true);
    this._showSsoButton();

    this.ssoEmailTarget.innerText = this.ssoEmail;

    const ssoSource = this._capitalizeSsoSource();
    this.ssoLoginSubtitleTextTarget.innerText = I18n.t("auth.previous_sso_signup", { sso: ssoSource });
  }

  _showSignupFlow() {
    this.signUpEmailInputTarget.value = this.signInEmailInputTarget.value;
    this._toggleElementVisibility(this.signupWithPasswordTarget, false);
  }

  _showSsoButton() {
    const ssoButtonMapping = {
      google: this.googleSsoButtonTarget,
      apple: this.appleSsoButtonTarget,
    };

    const ssoButton = ssoButtonMapping[this.ssoSource];
    if (ssoButton) {
      this._toggleElementVisibility(ssoButton, false);
    }
  }

  reset() {
    this._hideElements(this.defaultHideTargets)
    this._showElements(this.defaultShowTargets)
    this.continueWithEmailButtonTarget.disabled = false;

    this.validEmail = false;
    this.emailInputValue = null;
    this.ssoSource = null;
    this.ssoEmail = null;
    this.continueWithEmailButtonTarget.innerHTML = this.continueWithEmailButtonInnerHTML;

    const inputFields = this.authFormTarget.querySelectorAll("input");
    inputFields.forEach(input => {
      input.value = ""
    })
  }

  enableContinueWithEmailButton() {
    this.emailInputValue = this.signInEmailInputTarget.value.trim();
    if (this.emailInputValue.length === 0)
      return;

    this.validEmail = EMAIL_RX.test(this.emailInputValue);

    this._validateEmailInput(this.emailInputValue);
    this.continueWithEmailButtonTarget.disabled = !this.validEmail;
  }

  togglePasswordVisibility(e) {
    const passwordInputField = e.currentTarget.previousElementSibling;
    const passwordVisibilityIcon = e.currentTarget.querySelector("i");
    const isTextType = passwordInputField.type === "text";
    passwordInputField.type = isTextType ? "password" : "text";
    passwordVisibilityIcon.classList.toggle("fa-eye-slash", !isTextType);
    passwordVisibilityIcon.classList.toggle("fa-eye", isTextType);
  }

  _validateEmailInput(email) {
    setTimeout(() => {
      if (email.length > 0 &&  !this.validEmail) {
        showFormInputError(this.signInEmailInputTarget, I18n.t("validation.invalid_email"));
      } else {
        hideFormInputError(this.signInEmailInputTarget);
      }
    }, 1500);
  }
  /**
   * END: Modal content control (tabbed navigation, etc).
   */

  /**
   * START: Element visibility control
   */
  _toggleElementVisibility(element, hidden) {
    if (!element) return;
    element.classList.toggle("tw-hidden", hidden);
  }

  _toggleElementsVisibility(hidden, ...elements) {
    elements.forEach(element => {
      if (Array.isArray(element)) {
        element.forEach(target => this._toggleElementVisibility(target, hidden));
      } else {
        this._toggleElementVisibility(element, hidden);
      }
    });
  }

  _hideElements(...elements) {
    this._toggleElementsVisibility(true, ...elements);
  }

  _showElements(...elements) {
    this._toggleElementsVisibility(false, ...elements);
  }
  /**
   * END: Element visibility control
   */

  /**
   * START: Sign up form validation
   */
  validate() {
    if (this.debouncedValidatePassword) {
      return this.debouncedValidatePassword();
    }

    this.debouncedValidatePassword = debounce(this._validatePassword, 100);
  }

  _validatePassword() {
    const password = this.signUpPasswordTarget.value;

    if (password.length < MIN_PASSWORD_LENGTH) {
      window.passwordVerified = false;
      showFormInputError(this.signUpPasswordTarget, I18n.t("validation.short_password"));
    } else if (password.length > MAX_PASSWORD_LENGTH) {
      window.passwordVerified = false;
      showFormInputError(this.signUpPasswordTarget, I18n.t("validation.long_password"));
    } else if (!PASSWORD_RX.test(password)) {
      window.passwordVerified = false;
      showFormInputError(this.signUpPasswordTarget, I18n.t("validation.strong_password"));
    } else {
      hideFormInputError(this.signUpPasswordTarget);
      window.passwordVerified = true;
    }

    if (window.captchaVerified === true && window.passwordVerified === true) {
      this.signUpSubmitTarget.disabled = false;
    } else {
      this.signUpSubmitTarget.disabled = true;
    }
  }
  /**
   * END: Sign up form validation
   */

  _trackSelectSignInCTAEvent(e) {
    unconditionalTrackEvent(
      SELECT_SIGN_IN_CTA_EVENT,
      { "origin": e?.currentTarget?.dataset?.analyticsOrigin }
    );
  }
}
